import React from "react";
import ChannelMastersCard from "../Components/ChannelMastersCard";
import ConfigureThresholdPanel from "../Components/ConfigureThresholdPanel";
import DigestFrequencyCard from "../Components/DigestFrequencyCard";
import NotificationSectionCard from "../Components/NotificationSectionCard";
import PreferredLanguageCard from "../Components/PreferredLanguageCard";
import { useNotificationPreferences } from "../Hooks/UseNotificationPreferences";
import type {
  NotificationSection,
  NotificationSettingsItem,
} from "../Types/NotificationTypes";
import "../Styles/NotificationPage.scss";
import "../Styles/NotificationPreferences.scss";

const NotificationPreferences: React.FC = () => {
  const {
    t,
    i18n,
    sections,
    expanded,
    options,
    loading,
    digestFrequency,
    savingMaster,
    preferredNotificationLanguage,
    savingLanguage,
    channelMasters,
    configureTarget,
    form,
    setForm,
    saving,
    deletingRuleId,
    configurePanelRef,
    availablePlatforms,
    availableCampaigns,
    socialPlatforms,
    availableCities,
    cityAvailable,
    citySearch,
    availableProducts,
    productAvailable,
    productSearch,
    frequencyOptions,
    digestNote,
    hasNoConfiguredNotifications,
    toggleSection,
    handlePreferredLanguageChange,
    handleOpenConfigure,
    handleToggle,
    handleMasterToggleChange,
    handleDeleteRule,
    handleSaveThreshold,
    handlePlatformsChange,
    handleCampaignsChange,
    handleSocialPlatformsChange,
    handleTargetsChange,
    handleCitiesChange,
    handleProductsChange,
    closeConfigure,
    setCampaignSearch,
    setCitySearch,
    setProductSearch,
  } = useNotificationPreferences();

  const renderConfigurePanel = (
    section: NotificationSection,
    item: NotificationSettingsItem,
    sectionIdx: number,
    itemIdx: number
  ): React.ReactNode => (
    <ConfigureThresholdPanel
      section={section}
      item={item}
      sectionIdx={sectionIdx}
      itemIdx={itemIdx}
      form={form}
      options={options}
      saving={saving}
      availablePlatforms={availablePlatforms}
      availableCampaigns={availableCampaigns}
      socialPlatforms={socialPlatforms}
      availableCities={availableCities}
      cityAvailable={cityAvailable}
      citySearch={citySearch}
      availableProducts={availableProducts}
      productAvailable={productAvailable}
      productSearch={productSearch}
      configurePanelRef={configurePanelRef}
      setForm={setForm}
      onClose={closeConfigure}
      onSave={handleSaveThreshold}
      onPlatformsChange={handlePlatformsChange}
      onCampaignsChange={handleCampaignsChange}
      onCampaignSearch={setCampaignSearch}
      onSocialPlatformsChange={handleSocialPlatformsChange}
      onTargetsChange={handleTargetsChange}
      onCitiesChange={handleCitiesChange}
      onCitySearch={setCitySearch}
      onProductsChange={handleProductsChange}
      onProductSearch={setProductSearch}
    />
  );

  if (loading) {
    return (
      <div className="preferences-loading">
        <div className="preferences-loading__spinner" />
      </div>
    );
  }

  return (
    <div className="notification-preferences-container">
      <h1 className="notifications-page-title">
        {t("notificationPreferences.title")}
      </h1>
      <span className="notifications-page-description">
        {t("notificationPreferences.subtitle", {
          defaultValue:
            "Customize how you receive notifications and define when alerts should be triggered.",
        })}
      </span>
      <span className="notifications-page-hint">
        {t("notificationPreferences.hint", {
          defaultValue:
            "Turn on notification channels and configure thresholds only where you need custom alert rules.",
        })}
      </span>

      <DigestFrequencyCard
        digestFrequency={digestFrequency}
        frequencyOptions={frequencyOptions}
        digestNote={digestNote}
      />

      <ChannelMastersCard
        channelMasters={channelMasters}
        hasNoConfiguredNotifications={hasNoConfiguredNotifications}
        savingMaster={savingMaster}
        onMasterToggleChange={handleMasterToggleChange}
      />

      <PreferredLanguageCard
        preferredNotificationLanguage={preferredNotificationLanguage}
        savingLanguage={savingLanguage}
        onLanguageChange={handlePreferredLanguageChange}
      />

      {sections.map((section, sectionIdx) => (
        <NotificationSectionCard
          key={section.system}
          section={section}
          sectionIdx={sectionIdx}
          expanded={expanded[sectionIdx]}
          configureTarget={configureTarget}
          deletingRuleId={deletingRuleId}
          language={i18n.language}
          onToggleSection={toggleSection}
          onToggle={handleToggle}
          onConfigure={handleOpenConfigure}
          onDelete={handleDeleteRule}
          renderConfigurePanel={renderConfigurePanel}
        />
      ))}
    </div>
  );
};

export default NotificationPreferences;
